import { t } from 'i18next';

import { TreeItemType } from '@features/campaigns/builder/containers/FormPages/FormPages.types.ts';
import { getGeoTargetingConfig } from '@features/campaigns/shared/config/geoTargetingConfig.ts';
import {
  AdProductVideoCreativeStatus,
  CampaignDetails,
  CampaignMediaType,
  CampaignStatus,
  CampaignType,
  CreativeService,
  InvalidSectionsItem,
  Operation,
  OperationType,
} from '@features/campaigns/shared/types/campaign';
import {
  isAdProductDisplayNonNull,
  isAdProductVideoCreativeExistsInDB,
  isAdProductVideoInternal,
  isAdProductVideoNonNull,
  isAdProductVideoSelfService,
} from '@features/campaigns/shared/types/typeGuards.ts';
import { appendGeoTypeSuffix } from '@features/campaigns/shared/utils/appendGeoTypeSuffix.ts';
import { AccessRight } from '@shared/api/commonApiInterfaces';
import { ICON_VARIANTS, IconVariants } from '@shared/components/Icon/IconMapper';
import { i18nNameSpace } from '@shared/consts/i18n';
import { DataStatuses } from '@shared/types/common.ts';
import { UserCountryCode, UserCountryCodes } from '@shared/types/countryCodes.ts';

export interface HighlightData {
  label: `builder.steps.step05.highlights.${string}`;
  value?: number | string;
  valueRange?: number[];
  isMoneyValue: boolean;
  iconVariant: IconVariants;
}

export interface CampaignDetailsData {
  advertiserName: string;
  advertiserCode: string;
  brandName: string;
  brandCode: string;
  billingContactName: string;
  billingContactCode: string;
  startDate: string;
  endDate: string;
  budget: number;
  isAgencyCommissionGross: boolean;
  campaignName: string;
  createdBy: string;
  country: UserCountryCode;
}

export type TargetData =
  | {
      label: `review.labels.${string}`;
      variant?: Operation | null;
      items?: string[];
    }
  | {
      label: 'review.labels.contentPackage';
      variant?: typeof OperationType.INCLUDE;
      items?: string;
    };

export interface AdProductsData {
  isDisplayAdProductSelected: boolean;
  isVideoAdProductSelected: boolean;
  formatType: string;
  creativeServices: string;
  numberOfCreatives?: number | string;
  videoProductsIncluded?: string;
  lShapeImageUrl?: string | null;
  landingImageUrl?: string | null;
  switchInLeftImageUrl?: string | null;
  switchInBottomImageUrl?: string | null;
  videoSpotUrl?: string | null;
  videoSpotFilename?: string;
  trackingUrl?: string;
  videoSpotStatus: AdProductVideoCreativeStatus;
}

export interface ReviewData {
  highlights: HighlightData[];
  campaignDetails: CampaignDetailsData;
  target: TargetData[];
  adProducts: AdProductsData;
  additionalNote: string;
  invalidSections?: InvalidSectionsItem[];
  campaignStatus: CampaignStatus;
  accessRights: AccessRight[];
  mediaType: CampaignMediaType;
  type: CampaignType;
  country: UserCountryCode;
}

interface FormatTypeFunctionParams {
  adProductVideo: CampaignDetails['adProductVideo'];
  adProductDisplay: CampaignDetails['adProductDisplay'];
}

interface Targeting {
  interestSegments: TargetData;
  geo: TargetData;
  ageGender: TargetData;
  contentPackage: TargetData;
}

function composeTargetingByCountry(targeting: Targeting, country: UserCountryCode): TargetData[] {
  switch (country) {
    case UserCountryCodes.ES: {
      return [{ ...targeting.interestSegments }, { ...targeting.geo }, { ...targeting.ageGender }];
    }
    default: {
      return [
        { ...targeting.interestSegments },
        { ...targeting.geo },
        { ...targeting.ageGender },
        { ...targeting.contentPackage },
      ];
    }
  }
}

function formatFormatType({ adProductVideo, adProductDisplay }: FormatTypeFunctionParams) {
  const formatTypes: string[] = [];

  if (!adProductVideo && !adProductDisplay) {
    return '-';
  }

  if (adProductVideo) {
    formatTypes.push(t('builder.formFields.formatType.video', { ns: i18nNameSpace.CAMPAIGNS }));
  }

  if (adProductDisplay) {
    formatTypes.push(t('builder.formFields.formatType.display', { ns: i18nNameSpace.CAMPAIGNS }));
  }

  return formatTypes.join(', ');
}

function formatCreativeService(creativeServices?: CreativeService) {
  if (!creativeServices) {
    return '-';
  }

  return t(`builder.formFields.creativeServices.${creativeServices.toLowerCase()}`, { ns: i18nNameSpace.CAMPAIGNS });
}

function formatVideoProductsIncluded(adProductVideo: CampaignDetails['adProductVideo'] | null) {
  const videoProducts: string[] = [];

  if (!adProductVideo) {
    return '-';
  }

  // Only AdProductVideoInternal has vod and linearAddressable properties
  if (isAdProductVideoInternal(adProductVideo)) {
    if (adProductVideo.data.vod) {
      videoProducts.push(t('builder.formFields.videoProductsIncluded.vod', { ns: i18nNameSpace.CAMPAIGNS }));
    }

    if (adProductVideo.data.linearAddressable) {
      videoProducts.push(
        t('builder.formFields.videoProductsIncluded.linearAddressable', { ns: i18nNameSpace.CAMPAIGNS }),
      );
    }
  }

  return videoProducts.length > 0 ? videoProducts.join(', ') : '-';
}

export function reviewDataDTO(data: CampaignDetails): ReviewData {
  const geoTargetingConfig = getGeoTargetingConfig(data.country);
  const provincesList =
    data.provinces?.items.map(({ name }) => appendGeoTypeSuffix(name, TreeItemType.Province, geoTargetingConfig)) || [];

  const citiesList =
    data.cities?.items.map(({ name }) => appendGeoTypeSuffix(name, TreeItemType.City, geoTargetingConfig)) || [];

  let highlights: HighlightData[] = [
    {
      label: 'builder.steps.step05.highlights.impressions',
      value: data.pricing?.impressions,
      isMoneyValue: false,
      iconVariant: ICON_VARIANTS.eye,
    },
    {
      label: 'builder.steps.step05.highlights.estimatedReach',
      valueRange: data.pricing?.estimatedReachRange,
      isMoneyValue: false,
      iconVariant: ICON_VARIANTS.pieChart,
    },
    {
      label: 'builder.steps.step05.highlights.targetSize',
      value: data.pricing?.targetSize,
      isMoneyValue: false,
      iconVariant: ICON_VARIANTS.user,
    },
    {
      label: 'builder.steps.step05.highlights.estimatedFrequency',
      valueRange: data.pricing?.estimatedFrequencyRange,
      isMoneyValue: false,
      iconVariant: ICON_VARIANTS.clock,
    },
    {
      label: 'builder.steps.step05.highlights.cpm',
      value: data.pricing?.cpm,
      isMoneyValue: true,
      iconVariant: ICON_VARIANTS.barChart,
    },
    {
      label: 'builder.steps.step05.highlights.budget',
      value: data.budget,
      isMoneyValue: true,
      iconVariant: ICON_VARIANTS.wallet,
    },
  ];

  if (data.inventory) {
    highlights = [
      {
        label: 'builder.steps.step05.highlights.inventory',
        value: 'availabilityAndCosts.active',
        isMoneyValue: false,
        iconVariant: ICON_VARIANTS.activity,
      },
      ...highlights,
    ];
  }

  if (data.pricing?.bonusPrice) {
    highlights = [
      {
        label: 'builder.steps.step05.highlights.bonusCampaign',
        value: 'availabilityAndCosts.active',
        isMoneyValue: false,
        iconVariant: ICON_VARIANTS.gift,
      },
      ...highlights,
    ];
  }

  if (!data.pricing?.bonusPrice && data.pricing?.bonusImpressions > 0) {
    highlights = [
      {
        label: 'builder.steps.step05.highlights.bonusImpressions',
        value: data.pricing?.bonusImpressions,
        isMoneyValue: false,
        iconVariant: ICON_VARIANTS.gift,
      },
      ...highlights,
    ];
  }

  const targetingSections = {
    interestSegments: {
      label: 'review.labels.interestSegments',
      variant: data.interestSegments?.operationType,
      items: data.interestSegments?.items.map(({ name }) => name),
    },
    geo: {
      label: 'review.labels.geo',
      variant: data.provinces?.operationType || data.cities?.operationType,
      items: provincesList.concat(citiesList),
    },
    ageGender: {
      label: 'review.labels.ageAndGender',
      variant: data.ageGender?.operationType,
      items: data.ageGender?.items.map(({ name }) => name),
    },
    contentPackage: {
      label: 'review.labels.contentPackage',
      variant: data.contentPackage?.name ? OperationType.INCLUDE : undefined,
      items: data.contentPackage?.name,
    },
  } satisfies Targeting;

  return {
    highlights,
    campaignDetails: {
      advertiserName: data.advertiser?.name || '',
      advertiserCode: data.advertiser?.code || '',
      brandName: data.brand?.name || '',
      brandCode: data.brand?.code || '',
      billingContactName: data.billingContact?.name || '',
      billingContactCode: data.billingContact?.code || '',
      startDate: data.startDate,
      endDate: data.endDate,
      budget: data.budget,
      isAgencyCommissionGross: data.isAgencyCommissionGross,
      campaignName: data.name,
      createdBy: data.createdBy,
      country: data.country,
    },
    target: composeTargetingByCountry(targetingSections, data.country),
    adProducts: {
      isDisplayAdProductSelected: isAdProductDisplayNonNull(data.adProductDisplay),
      isVideoAdProductSelected: isAdProductVideoNonNull(data.adProductVideo),
      formatType: formatFormatType({
        adProductVideo: data.adProductVideo,
        adProductDisplay: data.adProductDisplay,
      }),
      creativeServices: formatCreativeService(data.adProductDisplay?.data.creativeServices),
      numberOfCreatives: data.adProductDisplay?.data.numberOfCreatives || '-',
      videoProductsIncluded: formatVideoProductsIncluded(data.adProductVideo),
      lShapeImageUrl: data.adProductDisplay?.lShapeImageUrl,
      landingImageUrl: data.adProductDisplay?.landingImageUrl,
      switchInLeftImageUrl: data.adProductDisplay?.leftLShapeImageUrl,
      switchInBottomImageUrl: data.adProductDisplay?.bottomLShapeImageUrl,
      videoSpotUrl:
        isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
          ? data.adProductVideo.creative.url
          : null,
      videoSpotFilename:
        isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
          ? data.adProductVideo.creative.originalFilename
          : 'review.creatives.videoSpot',
      trackingUrl:
        isAdProductVideoSelfService(data.adProductVideo) && data.adProductVideo.data.trackingUrl !== ''
          ? data.adProductVideo.data.trackingUrl
          : '-',
      videoSpotStatus:
        isAdProductVideoCreativeExistsInDB(data.adProductVideo) && data.adProductVideo.creative
          ? data.adProductVideo.creative.status
          : DataStatuses.PENDING,
    },
    additionalNote: data.additionalNote,
    invalidSections: data.invalidSections,
    campaignStatus: data.status,
    accessRights: data.accessRights,
    mediaType: data.mediaType,
    type: data.type,
    country: data.country,
  };
}
